import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import MicIcon from "@mui/icons-material/Mic";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import ArticleIcon from "@mui/icons-material/Article";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SpeedIcon from "@mui/icons-material/Speed";
import PersonalizeIcon from "@mui/icons-material/Accessibility";
import SmartToyIcon from "@mui/icons-material/SmartToy";

const PodcastGeneration = () => {
  const [openImage, setOpenImage] = useState(null);

  // Project data
  const project = {
    title: "AI-Powered Podcast Generation",
    subtitle: "Automating Audio Content Creation at Scale",
    overview:
      "I developed an automated podcast generation system for Liulishuo that transforms text articles into fully-produced audio content with accompanying comprehension materials. This system addresses the critical need for diverse, engaging listening content customized to users' interests and proficiency levels, significantly scaling our content production capabilities.",
    challenges: [
      "Provide personalized content based on user interests",
      "Offer materials across multiple difficulty levels",
      "Engage users more effectively with dynamic audio content",
      "Scale content production beyond human capacity",
    ],
    contentAnalysis: [
      "Automatically identifies key points and interesting angles from source articles",
      "Selects appropriate AI host personalities from our database based on content topic, tone, and complexity",
      "Matches host expertise and personality to content type for authentic presentation",
      "Generates engaging hooks and introductions to capture listener attention",
    ],
    scriptGeneration: [
      "Creates structured podcast scripts following proven formats",
      "Adapts content structure based on topic and intended audience",
      "Ensures natural conversational flow between hosts",
      "Maintains educational value while keeping content engaging",
    ],
    educationalEnhancement: [
      "Analyzes and categorizes content into appropriate difficulty levels (easy, medium, difficult)",
      "Generates targeted listening comprehension questions",
      "Creates supplementary materials to reinforce learning objectives",
      "Ensures content aligns with language acquisition principles",
    ],
    productionAutomation: [
      "Utilizes state-of-the-art text-to-speech technology for natural-sounding voices",
      "Automatically inserts appropriate background music and sound effects",
      "Generates custom cover images that reflect content themes",
      "Packages all elements into distribution-ready formats",
    ],
    results: [
      {
        metric: "100x",
        description: "Increase in daily content production capacity",
      },
      {
        metric: "Personalized",
        description: "Content delivery based on user interests",
      },
      {
        metric: "Consistent",
        description: "Quality across all generated materials",
      },
      {
        metric: "Reduced",
        description: "Production costs and resource requirements",
      },
    ],
    futureDevelopment: [
      "Adapting the system to create customized reading lessons",
      "Expanding the host personality database for greater content variety",
      "Implementing user feedback loops to refine content generation",
      "Developing more sophisticated difficulty leveling algorithms",
    ],
    images: [
      {
        src: "/images/podcast/create_article.png",
        title: "Article Selection Interface",
        description:
          "Dashboard for selecting source articles for podcast generation",
      },
      {
        src: "/images/podcast/content_list.png",
        title: "Content Management",
        description:
          "Overview of generated podcasts with metadata and analytics",
      },
      {
        src: "/images/podcast/title_output.png",
        title: "Generated Podcast Titles",
        description: "AI-generated engaging titles based on article content",
      },
      {
        src: "/images/podcast/questions.png",
        title: "Comprehension Questions",
        description: "Automatically generated questions to reinforce learning",
      },
      {
        src: "/images/podcast/audio_output.png",
        title: "Audio Production Interface",
        description: "Final audio output with waveform visualization",
      },
    ],
    audio: "/audio/sample_audio.mp3",
  };

  // Image modal handlers
  const handleImageClick = (img) => setOpenImage(img);
  const handleClose = () => setOpenImage(null);

  // Dark gray color constants
  const darkGray = "#212121";
  const darkerGray = "#1a1a1a";
  const accentColor = "#bb86fc"; // Purple accent color

  return (
    <>
      {/* Image Lightbox Dialog */}
      <Dialog open={!!openImage} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#111" }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          {openImage && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
              }}
            >
              <img
                src={openImage.src}
                alt={openImage.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  width: "auto",
                  height: "auto",
                  borderRadius: 8,
                  boxShadow: "0 4px 32px #000a",
                  background: "#222",
                }}
              />
              <Typography variant="h6" color="#fff" sx={{ mt: 2 }}>
                {openImage.title}
              </Typography>
              <Typography variant="body2" color="#ccc" sx={{ mt: 1, mb: 1 }}>
                {openImage.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Header */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          background: "linear-gradient(135deg, #1a1a1a 0%, #212121 100%)",
          color: "white",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {project.title}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, opacity: 0.9 }}>
              {project.subtitle}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
              {project.overview}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={<HeadphonesIcon />}
                label="Audio Content"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(187,134,252,0.2)" }}
              />
              <Chip
                icon={<AutorenewIcon />}
                label="Content Automation"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(187,134,252,0.2)" }}
              />
              <Chip
                icon={<SmartToyIcon />}
                label="AI Content Generation"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(187,134,252,0.2)" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Audio Demo */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: darkGray }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, color: "white" }}>
          Sample Generated Podcast
        </Typography>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 800,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.05)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                cursor: "pointer",
                mb: 3,
                width: "100%",
                maxWidth: 400,
              }}
              onClick={() => handleImageClick(project.images[4])}
            >
              <img
                src={project.images[4].src}
                alt={project.images[4].title}
                style={{
                  width: "100%",
                  borderRadius: "8px",
                }}
              />
            </Box>
            <audio
              controls
              style={{
                width: "100%",
                borderRadius: 8,
                background: "#000",
                display: "block",
              }}
            >
              <source src={project.audio} type="audio/mp3" />
              Your browser does not support the audio element.
            </audio>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.7)",
                mt: 2,
                textAlign: "center",
              }}
            >
              This sample demonstrates the quality of our AI-generated podcast
              content
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* The Challenge */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: darkGray, color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          The Challenge
        </Typography>
        <Typography variant="body1" paragraph>
          Liulishuo needed to dramatically increase its listening content
          library to meet user demands. Traditional content creation methods
          were too slow and resource-intensive, creating a bottleneck in our
          product development.
        </Typography>
        <List>
          {project.challenges.map((challenge, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: accentColor }} />
              </ListItemIcon>
              <ListItemText primary={challenge} sx={{ color: "white" }} />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Technical Solution Overview */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: darkGray, color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Technical Solution
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              I designed a comprehensive, one-click podcast generation system
              that handles the entire production pipeline:
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Card
                elevation={2}
                sx={{ bgcolor: "rgba(255,255,255,0.05)", color: "white" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, display: "flex", alignItems: "center" }}
                  >
                    <ArticleIcon sx={{ mr: 1, color: accentColor }} /> Content
                    Analysis & Planning
                  </Typography>
                  <List dense>
                    {project.contentAnalysis.map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: accentColor }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={item} sx={{ color: "white" }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              <Card
                elevation={2}
                sx={{ bgcolor: "rgba(255,255,255,0.05)", color: "white" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, display: "flex", alignItems: "center" }}
                  >
                    <MicIcon sx={{ mr: 1, color: accentColor }} /> Script
                    Generation
                  </Typography>
                  <List dense>
                    {project.scriptGeneration.map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: accentColor }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={item} sx={{ color: "white" }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Card
                elevation={2}
                sx={{ bgcolor: "rgba(255,255,255,0.05)", color: "white" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, display: "flex", alignItems: "center" }}
                  >
                    <MenuBookIcon sx={{ mr: 1, color: accentColor }} />{" "}
                    Educational Enhancement
                  </Typography>
                  <List dense>
                    {project.educationalEnhancement.map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: accentColor }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={item} sx={{ color: "white" }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              <Card
                elevation={2}
                sx={{ bgcolor: "rgba(255,255,255,0.05)", color: "white" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, display: "flex", alignItems: "center" }}
                  >
                    <HeadphonesIcon sx={{ mr: 1, color: accentColor }} />{" "}
                    Production Automation
                  </Typography>
                  <List dense>
                    {project.productionAutomation.map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: accentColor }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={item} sx={{ color: "white" }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* System Workflow */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: darkGray, color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          System Workflow
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                cursor: "pointer",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={() => handleImageClick(project.images[0])}
            >
              <Box sx={{ flex: 1 }}>
                <img
                  src={project.images[0].src}
                  alt={project.images[0].title}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ mt: 2 }}>
                1. {project.images[0].title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {project.images[0].description}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                cursor: "pointer",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={() => handleImageClick(project.images[2])}
            >
              <Box sx={{ flex: 1 }}>
                <img
                  src={project.images[2].src}
                  alt={project.images[2].title}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ mt: 2 }}>
                2. {project.images[2].title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {project.images[2].description}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                cursor: "pointer",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={() => handleImageClick(project.images[1])}
            >
              <Box sx={{ flex: 1 }}>
                <img
                  src={project.images[1].src}
                  alt={project.images[1].title}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ mt: 2 }}>
                3. {project.images[1].title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {project.images[1].description}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Educational Materials */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: darkGray, color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Educational Materials Generation
        </Typography>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              Beyond audio content, the system automatically generates
              educational materials to enhance the learning experience:
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: accentColor }} />
                </ListItemIcon>
                <ListItemText
                  primary="Comprehension questions tailored to content difficulty"
                  sx={{ color: "white" }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: accentColor }} />
                </ListItemIcon>
                <ListItemText
                  primary="Key vocabulary with definitions and usage examples"
                  sx={{ color: "white" }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: accentColor }} />
                </ListItemIcon>
                <ListItemText
                  primary="Discussion prompts to encourage deeper engagement"
                  sx={{ color: "white" }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: accentColor }} />
                </ListItemIcon>
                <ListItemText
                  primary="Transcripts with highlighted language points"
                  sx={{ color: "white" }}
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
              }}
              onClick={() => handleImageClick(project.images[3])}
            >
              <img
                src={project.images[3].src}
                alt={project.images[3].title}
                style={{
                  width: "100%",
                  maxWidth: 500,
                  borderRadius: "8px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.7)",
                mt: 2,
                textAlign: "center",
              }}
            >
              Automatically generated comprehension questions reinforce learning
              objectives
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Results and Impact */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: "linear-gradient(135deg, #1a1a1a 0%, #212121 100%)",
          color: "white",
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Results and Impact
        </Typography>
        <Typography variant="body1" paragraph>
          The AI Podcast Generator has transformed our content production
          capabilities:
        </Typography>
        <Grid container spacing={3}>
          {project.results.map((result, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "rgba(187,134,252,0.1)",
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  gutterBottom
                  color={accentColor}
                >
                  {result.metric}
                </Typography>
                <Typography variant="body1">{result.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Future Development
          </Typography>
          <Typography variant="body1" paragraph>
            Building on this success, we are expanding the system's
            capabilities:
          </Typography>
          <Grid container spacing={2}>
            {project.futureDevelopment.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "rgba(255,255,255,0.05)",
                    borderRadius: 2,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CheckCircleIcon sx={{ color: accentColor, mr: 1 }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box
          sx={{
            mt: 4,
            p: 3,
            bgcolor: "rgba(187,134,252,0.05)",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" align="center">
            This project demonstrates how AI can solve complex content scaling
            challenges while maintaining educational quality and
            engagement—transforming what was once a production bottleneck into a
            strategic advantage for our platform.
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

export default PodcastGeneration;
